import type { VariantProps } from "class-variance-authority"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"
import * as React from "react"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 hover:shadow-md",
        primary: "bg-primary text-primary-foreground shadow-sm",
        destructive: "bg-destructive text-white shadow-sm hover:translate-y-[-1px] hover:bg-destructive/95 hover:shadow-md active:translate-y-[0px]",
        outline: "border border-solid border-input bg-background text-foreground hover:border-foreground hover:bg-background/90 active:bg-background/80 dark:hover:border-foreground dark:hover:bg-background/20 dark:active:bg-background/10",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 active:bg-secondary/70",
        expandIcon: "group relative bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80",
        ringHover:
          "bg-primary text-primary-foreground transition-all duration-300 hover:bg-primary/90 hover:ring-2 hover:ring-primary/90 active:bg-primary/80",
        gooeyRight:
          "relative z-0 overflow-hidden bg-primary from-zinc-400 text-primary-foreground transition-all duration-500 before:absolute before:inset-0 before:-z-10 before:translate-x-[150%] before:translate-y-[150%] before:scale-[2.5] before:rounded-[100%] before:bg-gradient-to-r before:transition-transform before:duration-1000  hover:before:translate-x-[0%] hover:before:translate-y-[0%] ",
        gooeyLeft:
          "relative z-0 overflow-hidden bg-primary from-zinc-400 text-primary-foreground transition-all duration-500 after:absolute after:inset-0 after:-z-10 after:translate-x-[-150%] after:translate-y-[150%] after:scale-[2.5] after:rounded-[100%] after:bg-gradient-to-l after:transition-transform after:duration-1000  hover:after:translate-x-[0%] hover:after:translate-y-[0%] ",
        ghost: "hover:bg-accent hover:text-accent-foreground active:bg-accent/80",
        link: "text-primary underline-offset-4 hover:underline",
        light: "border border-solid border-gray-200 bg-white text-black shadow-sm hover:translate-y-[-1px] hover:bg-gray-50 hover:shadow-md active:translate-y-[0px] dark:border-neutral-700",
        dark: "bg-black text-white shadow-sm hover:translate-y-[-1px] hover:bg-gray-900 hover:shadow-md active:translate-y-[0px]",
        success: "bg-green-600 text-white hover:bg-green-600/80 active:bg-green-600/70",
        warning: "bg-yellow-500 text-white hover:bg-yellow-500/80 active:bg-yellow-500/70",
        headless: "text-foreground",
        rounded: "rounded-full bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80",
        gradient: "bg-gradient-to-r from-primary to-primary/80 text-primary-foreground shadow-sm hover:translate-y-[-1px] hover:from-primary/90 hover:to-primary/70 hover:shadow-md active:translate-y-[0px] active:from-primary/80 active:to-primary/60",
        soft: "bg-primary/10 text-primary hover:bg-primary/20 active:bg-primary/30",
        glass: "border border-white/30 bg-white/20 text-black shadow-sm backdrop-blur-md hover:bg-white/30 hover:shadow-md dark:text-white",
        muted: "bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300",
      },
      size: {
        "default": "h-9 px-4 py-2 text-sm has-[>svg]:px-3",
        "xs": "h-6 gap-1.5 rounded-md px-2 text-sm has-[>svg]:px-2",
        "sm": "h-8 gap-1.5 rounded-md px-3 text-sm has-[>svg]:px-2.5",
        "lg": "h-10 rounded-md px-6 text-sm has-[>svg]:px-4",
        "xl": "h-12 rounded-md px-8 text-base has-[>svg]:px-6",
        "2xl": "h-14 rounded-md px-10 text-lg has-[>svg]:px-8",
        "icon": "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      data-slot="button"
      {...props}
    />
  )
}

export { Button, buttonVariants }
